<template>
  <div class="mobile-counter">
    <h1 class="mt-2 mb-4">{{ counter.name }}</h1>
    <p v-if="loadError" role="alert">{{ loadError }}</p>
    <div v-if="pendingSession" class="counter-session" role="status">Найден сохранённый подсчёт на этом устройстве.
      <button @click="resumeSession">Продолжить</button><button @click="discardSession">Начать заново</button>
    </div>
    <p class="counter-save-note" role="status">{{ saveNotice }}</p>
    <div class="counter-actions" v-if="!showStart && !pendingSession">
      <button @click="undoCount" :disabled="!undoStack.length">Отменить последнее</button>
      <button @click="resetSession">Новый подсчёт</button>
      <span>Всего: {{ totalCount }} · Цикл {{ Math.min(currentCycle, numberOfCycles) }} из {{ numberOfCycles }}</span>
    </div>
    <div class="info-wrapper">
      <div class="info-block">
        <span class="counter-current-label">Клеток в текущем цикле</span>
        <div class="info-text">{{ infoText }}</div>
      </div>
    </div>
    <div class="d-flex align-items-center justify-content-start flex-grow-1 my-4" v-if="showStart && !pendingSession && !loadError">
      <button class="btn-labhub me-4" @click="startCounter()">
        <span class="d-inline">Начать</span>
      </button>
      Количество циклов:
      <input type="number" v-model="numberOfCycles" min="1" max="20" inputmode="numeric" class="n-counters ms-2" />
    </div>

    <div class="info-message-block mt-4" v-if="showEnd">
      Подсчет окончен. Теперь вы можете сформировать и скачать бланк!
    </div>

    <div class="info-wrapper-buttons mt-4">
      <div v-for="button in counter.buttons" :key="button.identButton">
        <button :class="{ clicked: button.clicked, 'btn-counter-items': true }"
          :disabled="isCompleted || !!pendingSession" :aria-label="button.nameButton" @click="incrementInfoText(button.nameButton)">
          {{ button.shotNameButton }}
        </button>
        <div class="ident-text">
          <img src="/key-info.png" alt="Info Icon" class="info-key" />
          {{ button.identButton }}
        </div>
      </div>
    </div>
    <div class="table-responsive mt-4 info-wrapper-buttons">
      <table class="table table-bordered">
        <thead>
          <tr>
            <th>Параметр</th>
            <th v-for="n in numberOfCycles" :key="n">
              <span v-if="numberOfCycles > 1">Цикл {{ n }}</span>
              <span v-else>Значение</span>
            </th>
            <th v-if="numberOfCycles > 1">Среднее</th>
            <th>Норма</th>
            <th>Ед. Измерения</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(buttonCategory, index) in buttonCounts" :key="'category-' + index">
            <tr>
              <td :colspan="4 + Number(numberOfCycles)">
                <b>{{ buttonCategory.category }}</b>
              </td>
            </tr>
            <template v-if="buttonCategory.buttons">
              <tr v-for="button in buttonCategory.buttons" :key="'button-' + button.name">
                <td>{{ button.name }} ({{ button.shot }})</td>
                <td v-for="n in numberOfCycles" :key="n">
                  {{ button["percentage_cycle_" + n] || 0 }}
                </td>
                <td v-if="numberOfCycles > 1">{{ averageValue(button) }}</td>
                <td>{{ button.normaButton }}</td>
                <td>{{ button.unitButton }}</td>
              </tr>
            </template>
          </template>
        </tbody>
      </table>
    </div>
    <button class="down-blanc mt-4" :disabled="!isOnline" title="Выгрузка бланка требует подключения к интернету" @click="openModal" v-if="showEnd">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
          d="M8.00016 2.66668C10.9402 2.66668 13.3335 5.06001 13.3335 8.00001C13.3335 10.94 10.9402 13.3333 8.00016 13.3333C5.06016 13.3333 2.66683 10.94 2.66683 8.00001C2.66683 5.06001 5.06016 2.66668 8.00016 2.66668ZM8.00016 1.33334C4.32016 1.33334 1.3335 4.32001 1.3335 8.00001C1.3335 11.68 4.32016 14.6667 8.00016 14.6667C11.6802 14.6667 14.6668 11.68 14.6668 8.00001C14.6668 4.32001 11.6802 1.33334 8.00016 1.33334ZM8.66683 8.00001V5.33334H7.3335V8.00001H5.3335L8.00016 10.6667L10.6668 8.00001H8.66683Z"
          fill="#6CB0FF" />
      </svg>
      <span class="ms-2">Скачать бланк</span>
    </button>
    <download-modal v-if="documentId" ref="downloadModal" :documentId="documentId" :tableData="tableData" />
  </div>
</template>

<script>
import http from '@/http';
import {online} from '@/utils/connectivity';
import DownloadModal from "@/components/DownloadModal.vue";
import { evaluate } from "mathjs";

export default {
  components: {
    DownloadModal,
  },
  props: ["id"],
  data() {
    return {
      counter: {},
      showModal: false,
      infoText: 0,
      buttonCounts: [],
      numberOfCycles: 1,
      currentCycle: 1,
      isCompleted: true,
      showEnd: false,
      showStart: true,
      documentId: null,
      indicators: [],
      cycleData: [],
      tableData: {},
      definitionKey: '',
      initialState: null,
      pendingSession: null,
      undoStack: [],
      saveNotice: '',
      loadError: '',
    };
  },
  computed: {
    isOnline(){return online.value;},
    totalCount() { return this.buttonCounts.reduce((n,c)=>n+c.buttons.reduce((m,b)=>m+(b.count||0),0),0); },
  },
  methods: {
    snapshot() {
      const keys=['infoText','buttonCounts','numberOfCycles','currentCycle','isCompleted','showEnd','showStart','indicators','cycleData','tableData'];
      return JSON.parse(JSON.stringify(Object.fromEntries(keys.map(k=>[k,this[k]]))));
    },
    applySnapshot(state) {
      for (const k of Object.keys(this.snapshot())) if (Object.prototype.hasOwnProperty.call(state,k)) this[k]=state[k];
    },
    saveSession() {
      try {
        localStorage.setItem('labhub-counter-'+this.id,JSON.stringify({version:1,definition:this.definitionKey,state:this.snapshot(),undo:this.undoStack}));
        this.saveNotice='Подсчёт сохранён только на этом устройстве, без ФИО и идентификаторов пациента.';
      } catch { this.saveNotice='Не удалось сохранить подсчёт на устройстве. Не закрывайте страницу до завершения.'; }
    },
    resumeSession() {
      this.applySnapshot(this.pendingSession.state);
      this.undoStack=this.pendingSession.undo || [];
      this.pendingSession=null;
      this.saveSession();
    },
    discardSession() {
      try {localStorage.removeItem('labhub-counter-'+this.id);}catch {/* Storage unavailable. */}
      this.pendingSession=null;this.undoStack=[];this.saveNotice='';
    },
    resetSession() {
      if(!confirm('Начать новый подсчёт? Текущие результаты на этом устройстве будут удалены.'))return;
      this.applySnapshot(this.initialState);this.discardSession();
    },
    undoCount() {
      if(!this.undoStack.length)return;
      this.applySnapshot(this.undoStack.pop());this.saveSession();
    },
    handleKeyDown(event) {
      if (this.isCompleted || this.pendingSession || event.repeat || /INPUT|TEXTAREA|SELECT/.test(event.target.tagName)) return;
      const pressedKey = event.key.toUpperCase();
      const button = this.counter.buttons.find(
        (btn) => btn.identButton === `Key${pressedKey}`
      );
      if (button) {
        this.incrementInfoText(button.nameButton);
      }
    },

    computeAllIndicators() {
      // Сортировка индикаторов по порядку расчета
      const sortedIndicators = [...this.indicators].sort((a, b) => a.numbetCalc - b.numbetCalc);
      sortedIndicators.forEach(indicator => {
        indicator["percentage_cycle_" + this.currentCycle] =
          this.computeIndicatorValue(indicator);
      });
    },

    averageComputedIndicator(indicator) {
      const values = Array.from({length: Number(this.numberOfCycles)}, (_, i) => indicator["percentage_cycle_" + (i + 1)]);
      if (!values.length || values.some(value => typeof value !== 'number' || !Number.isFinite(value))) return 'Не вычисляется';
      return this.roundToHundredths(values.reduce((sum, value) => sum + value, 0) / values.length);
    },

    computeIndicatorValue(indicator) {
      // Получаем формулу из индикатора
      const formula = indicator.formula;
      let computedFormula = formula;

      // Ищем все метки в формуле (в фигурных скобках)
      const matches = formula.match(/{(.*?)}/g) || [];
      for (const match of matches) {
        // Удаляем фигурные скобки, чтобы получить имя
        const name = match.replace(/{|}/g, "");

        let foundButton;
        for (const cat of this.buttonCounts) {
          foundButton = cat.buttons.find((btn) => btn.name === name);
          if (foundButton) break;
        }

        // Если кнопка найдена, заменяем метку на ее значение
        if (foundButton) {
          computedFormula = computedFormula.replace(
            match,
            foundButton[`count_cycle_${this.currentCycle}`] || 0
          );
        } else {
          // Если кнопка не найдена, ищем среди индикаторов
          const foundIndicator = this.indicators.find(
            (ind) => ind.name === name
          );
          // Если индикатор не найден или его значение еще не вычислено, пропускаем
          if (!foundIndicator || !Number.isFinite(foundIndicator["percentage_cycle_" + this.currentCycle])) {
            return null;
          }
          // Заменяем метку на значение индикатора
          computedFormula = computedFormula.replace(
            match,
            foundIndicator["percentage_cycle_" + this.currentCycle]
          );
          foundIndicator["computedFormula"] = computedFormula
        }
      }

      try {
        // Вычисляем значение формулы с использованием mathjs
        const result = evaluate(computedFormula);

        // Проверка результата на наличие бесконечности
        if (isFinite(result)) {
          return this.roundToHundredths(result);
        } else {
          console.warn(`Formula "${formula}" resulted in Infinity.`);
          return null;
        }
      } catch (error) {
        // В случае ошибок при вычислении формулы выводим ошибку
        console.error(`Failed to compute formula "${formula}"`, error);
        return null;
      }
    },

    averageIndicatorValue(indicator) {
      let total = 0;
      for (let i = 0; i <= this.numberOfCycles; i++) {
        total += indicator["value_cycle_" + i] || 0; // Предполагая, что у вас есть значения для каждого цикла в индикаторе
      }
      return this.roundToHundredths(total / this.numberOfCycles);
    },

    generateTableData() {
      const headers = ["Параметр"];

      headers.push("Значение");

      headers.push("Норма", "Ед. Измерения");

      const tableData = {
        headers: headers,
        categories: [],
      };

      for (const category of this.buttonCounts) {
        const categoryData = {
          categoryName: "Категория: " + category.category,
          data: [],
        };

        for (const button of category.buttons) {
          const rowData = [button.name];

          // if (this.numberOfCycles > 1) {
          //   rowData.push(this.averageValue(button) + "");
          // } else {
          //   rowData.push(button["percentage_cycle_1"]);
          // }
          rowData.push(this.averageValue(button));


          rowData.push(button.normaButton, button.unitButton);
          categoryData.data.push(rowData);
        }

        tableData.categories.push(categoryData);
      }

      for (const indicator of this.indicators) {
        const targetCategoryName =
          "Категория: " +
          (indicator.category === "Расчётные показатели"
            ? "Расчётные показатели"
            : indicator.category);
        const existingCategory = tableData.categories.find(
          (cat) => cat.categoryName === targetCategoryName
        );


        const indicatorData = [indicator.name];

        indicatorData.push(indicator.averageComputed, indicator.reference, indicator.units);

        if (existingCategory) {
          if (
            !existingCategory.data.some((data) => data[0] === indicator.name)
          ) {
            existingCategory.data.push(indicatorData);
          }
        } else {
          tableData.categories.push({
            categoryName: targetCategoryName,
            data: [indicatorData],
          });
        }
      }

      this.tableData = tableData;
    },

    openModal() {
      if (this.documentId) {
        this.$refs.downloadModal.show();
      }
    },
    startCounter() {
      if (!Number.isInteger(Number(this.numberOfCycles)) || this.numberOfCycles < 1 || this.numberOfCycles > 20) { this.saveNotice = 'Укажите от 1 до 20 циклов'; return; }
      this.numberOfCycles=Number(this.numberOfCycles);this.infoText=0;
      this.isCompleted = false;
      this.showStart = false;
      this.saveSession();
    },

    computeIndicatorPercentage(indicator) {
      let totalButtonCounts = 0;
      this.buttonCounts.forEach((category) => {
        category.buttons.forEach((button) => {
          totalButtonCounts += button.count;
        });
      });

      if (indicator.computedValue !== undefined && totalButtonCounts !== 0) {
        return this.roundToHundredths((indicator.computedValue / totalButtonCounts) * 100);
      }
      return 0;
    },

    averagePercentage(indicator) {
      let total = 0;
      for (let i = 0; i <= this.numberOfCycles; i++) {
        total += indicator["percentage_cycle_" + i] || 0;
      }
      return total / this.numberOfCycles;
    },

    incrementInfoText(buttonName) {
      if (this.isCompleted || this.pendingSession || !this.counter.buttons.some(b=>b.nameButton===buttonName)) return;
      this.undoStack.push(this.snapshot());
      if(this.undoStack.length>20)this.undoStack.shift();

      const clickedButton = this.counter.buttons.find(
        (btn) => btn.nameButton === buttonName
      );
      if (clickedButton) {
        clickedButton.clicked = !clickedButton.clicked; // Переключение состояния
        setTimeout(() => {
          clickedButton.clicked = !clickedButton.clicked; // Возвращение в исходное состояние
        }, 300);
      }

      this.infoText += 1;

      let existingButton = null;

      for (const category of this.buttonCounts) {
        existingButton = category.buttons.find(
          (btn) => btn.name === buttonName
        );
        if (existingButton) {
          break;
        }
      }

      if (existingButton) {
        existingButton["count_cycle_" + this.currentCycle] =
          (existingButton["count_cycle_" + this.currentCycle] || 0) + 1;
        existingButton.count = (existingButton.count || 0) + 1;
      }

      // Рассчитываем процентные значения для каждой кнопки
      this.buttonCounts.forEach((category) => {
        category.buttons.forEach((btn) => {
          btn["percentage_cycle_" + this.currentCycle] = this.roundToHundredths(
            ((btn["count_cycle_" + this.currentCycle] || 0) / this.infoText) *
            100
          );
        });
      });


      // Рассчитываем процентные значения для расчетных показателей
      this.computeAllIndicators();


      if (this.infoText === parseInt(this.counter.cond)) {
        this.currentCycle += 1;
        this.infoText = 0;
        this.resetCycleValues();
      }

      if (this.currentCycle > this.numberOfCycles) {
        this.isCompleted = true;
        this.showEnd = true;
        this.computeAllIndicators();
        for (const indicator of this.indicators) {
          indicator.averageComputed = this.averageComputedIndicator(indicator);
        }
        this.generateTableData();
      }

      this.saveSession();
    },

    roundToHundredths(num) {
      return Math.round(num * 1000) / 1000;
    },

    resetCycleValues() {
      // Обнуляем значения для кнопок для нового цикла
      this.buttonCounts.forEach(category => {
        category.buttons.forEach(button => {
          button[`count_cycle_${this.currentCycle}`] = 0;
          button[`percentage_cycle_${this.currentCycle}`] = 0;
        });
      });

      // Обнуляем значения для индикаторов для нового цикла
      this.indicators.forEach(indicator => {
        indicator[`percentage_cycle_${this.currentCycle}`] = 0;
      });
    },


    averageValue(button) {
      let total = 0;
      for (let i = 0; i <= this.numberOfCycles; i++) {
        total += button["percentage_cycle_" + i] || 0;
      }
      return this.roundToHundredths(total / this.numberOfCycles);
    },
  },
  async mounted() {
    try {
      const response = await http.get(
        `counters/${this.id}`
      );
      this.definitionKey=JSON.stringify(response.data);
      this.counter = response.data;
      this.documentId = response.data.document_id;
      this.indicators = response.data.indicators || [];
      // Инициализация buttonCounts с категориями и кнопками
      this.counter.buttons.forEach((button) => {
        const existingCategory = this.buttonCounts.find(
          (cat) => cat.category === button.category
        );
        if (existingCategory) {
          existingCategory.buttons.push({
            name: button.nameButton,
            shot: button.shotNameButton,
            count: 0,
            percentage: 0,
            normaButton: button.normaButton,
            unitButton: button.unitButton,
          });
        } else {
          this.buttonCounts.push({
            category: button.category,
            buttons: [
              {
                name: button.nameButton,
                shot: button.shotNameButton,
                count: 0,
                percentage: 0,
                normaButton: button.normaButton,
                unitButton: button.unitButton,
              },
            ],
          });
        }
      });
      this.initialState=this.snapshot();
      try {
        const saved=JSON.parse(localStorage.getItem('labhub-counter-'+this.id)||'null');
        if(saved && saved.version===1 && saved.state && Array.isArray(saved.state.buttonCounts) && Number.isInteger(saved.state.numberOfCycles) && saved.state.numberOfCycles>=1 && saved.state.numberOfCycles<=20 && Array.isArray(saved.undo)) {
          if(saved.definition===this.definitionKey)this.pendingSession=saved;
          else this.saveNotice='Определение счётчика обновилось. Предыдущий подсчёт не загружен; начните новый.';
        }
      }catch {this.saveNotice='Сохранённый подсчёт недоступен.';}
    } catch (error) {
      this.loadError='Не удалось загрузить счётчик. Подключитесь к сети и откройте его заранее для работы офлайн.';
    }

    window.addEventListener("keydown", this.handleKeyDown);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.handleKeyDown);
  },
};

</script>

<style scoped>
.info-wrapper {
  display: flex;
  width: 100%;
  height: 91px;
  padding: 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  border-radius: 5px;
  background: var(--design-color-white, #fff);
  box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.1);
}

.info-wrapper-buttons {
  margin-top: 40px;
  display: flex;
  width: 100%;
  padding: 25px;
  flex-direction: row;
  /* изменили с column на row */
  flex-wrap: wrap;
  /* добавили возможность переноса на новую линию */
  justify-content: flex-start;
  /* выравнивание элементов по началу линии */
  align-items: center;
  gap: 16px;
  /* отступы между элементами */
  flex-shrink: 0;
  border-radius: 5px;
  background: var(--design-color-white, #fff);
  box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.1);
}

.info-block {
  display: flex;
  width: 100%;
  align-items: center;
  border-radius: 5px;
  border: 1px solid #e5f0ff;
  background: #fafcff;
  height: 54px;
  justify-content: space-between;
  padding-left: 10px;
  padding-right: 10px;
}

.n-counters {
  display: flex;
  width: 60px;
  align-items: center;
  border-radius: 5px;
  border: 1px solid #e5f0ff;
  background: #ffffff;
  justify-content: space-between;
  padding-left: 10px;
  padding-right: 10px;
}

.info-icon {
  width: 29px;
  height: 34px;
  margin-right: 10px;
}

.info-text {
  color: #00133d;
  font-family: Nunito;
  font-size: 22px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
}

.ident-text {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  color: #afafaf;
  font-family: Nunito;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
}

.info-key {
  width: 15px;
}

.down-blanc {
  width: 168px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 10px;
  border: 1px solid #6cb0ff;
  background: #fff;
}

.down-blanc span {
  color: #6cb0ff;
  font-family: Nunito;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 100%;
  /* 14px */
}

.info-message-block {
  display: flex;
  width: 100%;
  height: 50px;
  padding: 0px 20px;
  align-items: center;
  gap: 17px;
  flex-shrink: 0;
  border-radius: 5px;
  background: #e3f0ff;
}

.btn-counter-items {
  min-width: 45px;
  height: 50px;
  flex-shrink: 0;
  border-radius: 5px;
  border: 1px solid #e5f0ff;
  background: #fafcff;
  position: relative;
  overflow: hidden;
}

.btn-counter-items:hover {
  background: #d3e0f4;
}

.btn-counter-items.clicked {
  animation: buttonClickAnimation 0.3s ease-in-out;
}

@keyframes buttonClickAnimation {
  0% {
    background-color: #fafcff;
  }

  50% {
    background-color: #6cb0ff;
    color: white;
  }

  100% {
    background-color: #fafcff;
  }
}
</style>
